import type { CodeSent, OpenedSession, PasscodeChannel, PasswordGrant } from '../types/domain.types';

import { PASSCODE_ENDPOINTS } from './endpoints';
import { request } from './http';
import { readOpenedSession } from './openedSession';
import { codeSentSchema, parseOrThrow, passwordSetSchema, resetGrantSchema } from './schemas';

export interface PasswordLoginInput {
  readonly identifier: string;
  readonly password: string;
}

export interface SetPasswordInput {
  readonly newPassword: string;
  readonly confirmPassword: string;
  readonly passwordGrant?: string;
}

export const passwordLogin = async (input: PasswordLoginInput, signal: AbortSignal | undefined): Promise<OpenedSession> => {
  const raw = await request('post', PASSCODE_ENDPOINTS.PASSWORD_LOGIN, {
    body: { identifier: input.identifier, password: input.password },
    signal,
  });
  return readOpenedSession(raw, 'password login');
};

export const setPassword = async (
  { newPassword, passwordGrant }: SetPasswordInput,
  signal: AbortSignal | undefined,
): Promise<void> => {
  const raw =
    passwordGrant === undefined
      ? await request('put', PASSCODE_ENDPOINTS.PASSWORD, { body: { newPassword }, signal })
      : await request('post', PASSCODE_ENDPOINTS.PASSWORD_RESET, { body: { newPassword, passwordGrant }, signal });
  parseOrThrow(passwordSetSchema, raw, 'set password');
};

export interface PasswordCodeTarget {
  readonly identifier: string;
  readonly channel?: PasscodeChannel;
}

export interface PasswordCodeCheck {
  readonly identifier: string;
  readonly code: string;
}

export const requestPasswordCode = async ({ identifier, channel }: PasswordCodeTarget, signal: AbortSignal | undefined): Promise<CodeSent> => {
  const body = channel === undefined ? { identifier, purpose: 'PASSWORD_RESET' } : { identifier, channel, purpose: 'PASSWORD_RESET' };
  const raw = await request('post', PASSCODE_ENDPOINTS.CODE, { body, signal });
  return parseOrThrow(codeSentSchema, raw, 'password code');
};

export const verifyPasswordCode = async ({ identifier, code }: PasswordCodeCheck, signal: AbortSignal | undefined): Promise<PasswordGrant> => {
  const raw = await request('post', PASSCODE_ENDPOINTS.VERIFY, { body: { identifier, code }, signal });
  return parseOrThrow(resetGrantSchema, raw, 'password code verify');
};
